import React, { useEffect, useState } from 'react';
import { colors } from '../../../../../styleSheet';
import {
  Typography,
  Box,
  Divider,
  LinearProgress,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
  Table,
  Paper,
  TableBody,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import ServiceHeader from './ServiceHeader';
import { DrawerLoading, DrawerEmpty } from './DrawerStates';
import {
  progressTableContainerSx,
  progressTableSx,
  progressHeaderCellSx,
  progressBodyRowSx,
} from '../progressTableStyles';
import { matchesSearch } from '../taskSearch';
import TaskSearchField from '../TaskSearchField';
import { DRAWER } from '../../../tokens';

function ProgressInfo(props) {
  const { node, getSessionProgress, sessionProgress, loadingSessionProgress } =
    props;
  const { session, date } = useParams();
  const [progress, setProgress] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [percentage, setPercentage] = useState(0);
  const [noData, setNoData] = useState(false);
  const [pendingFilter, setPendingFilter] = useState('');
  const [completedFilter, setCompletedFilter] = useState('');

  const visiblePending = progress.filter(
    (row) =>
      !row.completed &&
      matchesSearch(
        [
          row.aisle,
          row.batch_id,
          row.job_id,
          row.source,
          row.started_at,
          moment(row.started_at).fromNow(),
        ],
        pendingFilter
      )
  );
  const visibleCompleted = progress.filter(
    (row) =>
      row.completed &&
      matchesSearch(
        [
          row.aisle,
          row.batch_id,
          row.job_id,
          row.source,
          row.started_at,
          row.finished_at,
        ],
        completedFilter
      )
  );

  const getProgress = async () => {
    setLoadingProgress(true);
    const data = await getSessionProgress({
      session,
      process_id: node.process_id,
    });
    if (data.length === 0) {
      setNoData(true);
      setLoadingProgress(false);
    } else {
      const total = data.length;
      const completed = data.filter((task) => task.completed === true).length;
      setProgress(data);
      setPercentage(Math.round((completed / total) * 100));
      setLoadingProgress(false);
    }
  };

  useEffect(() => {
    getProgress();
  }, [session]);
  return (
    <Box>
      <ServiceHeader node={node} />
      {loadingProgress ? (
        <DrawerLoading />
      ) : (
        <Box>
          {noData ? (
            <DrawerEmpty>
              No tasks linked to this service on this session...
            </DrawerEmpty>
          ) : (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1,
                  marginY: 2,
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={percentage}
                  color={percentage !== 100 ? 'secondary' : 'success'}
                  sx={{ height: '10px', borderRadius: 1, width: '70%' }}
                />
                <Typography sx={{ color: 'lightgrey', fontSize: 16 }}>
                  {percentage}%
                </Typography>
              </Box>
              <Box sx={{ marginBottom: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mx: 2,
                    my: 1,
                  }}
                >
                  <Typography
                    sx={{ color: colors.lavender, fontWeight: 'bold', fontSize: 20 }}
                  >
                    Pending Tasks
                  </Typography>
                  <TaskSearchField
                    value={pendingFilter}
                    onChange={(e) => setPendingFilter(e.target.value)}
                    sx={{ m: 0, width: 240, flexShrink: 0 }}
                  />
                </Box>
                <TableContainer component={Paper} sx={progressTableContainerSx}>
                  <Table
                    stickyHeader
                    aria-label="simple table"
                    sx={progressTableSx}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={progressHeaderCellSx}>Aisle</TableCell>
                        <TableCell sx={progressHeaderCellSx}>
                          Batch ID
                        </TableCell>
                        <TableCell sx={progressHeaderCellSx}>Job ID</TableCell>
                        <TableCell sx={progressHeaderCellSx}>Source</TableCell>
                        <TableCell sx={progressHeaderCellSx}>
                          Started At
                        </TableCell>
                        <TableCell sx={progressHeaderCellSx}>
                          Processing Time
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visiblePending.map((row) => (
                        <TableRow key={row.name} sx={progressBodyRowSx}>
                          <TableCell>{row.aisle}</TableCell>
                          <TableCell>{row.batch_id}</TableCell>
                          <TableCell>{row.job_id}</TableCell>
                          <TableCell>{row.source}</TableCell>
                          <TableCell>{row.started_at}</TableCell>
                          <TableCell>
                            {moment(row.started_at).fromNow()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Divider sx={{ mx: 2, mb: 2, borderColor: DRAWER.border }} />
              <Box sx={{ marginBottom: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mx: 2,
                    my: 1,
                  }}
                >
                  <Typography
                    sx={{ color: colors.lavender, fontWeight: 'bold', fontSize: 20 }}
                  >
                    Completed Tasks
                  </Typography>
                  <TaskSearchField
                    value={completedFilter}
                    onChange={(e) => setCompletedFilter(e.target.value)}
                    sx={{ m: 0, width: 240, flexShrink: 0 }}
                  />
                </Box>
                <TableContainer component={Paper} sx={progressTableContainerSx}>
                  <Table
                    stickyHeader
                    aria-label="simple table"
                    sx={progressTableSx}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={progressHeaderCellSx}>Aisle</TableCell>
                        <TableCell sx={progressHeaderCellSx}>
                          Batch ID
                        </TableCell>
                        <TableCell sx={progressHeaderCellSx}>Job ID</TableCell>
                        <TableCell sx={progressHeaderCellSx}>Source</TableCell>
                        <TableCell sx={progressHeaderCellSx}>
                          Started At
                        </TableCell>
                        <TableCell sx={progressHeaderCellSx}>
                          Finished At
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleCompleted.map((row) => (
                        <TableRow key={row.name} sx={progressBodyRowSx}>
                          <TableCell>{row.aisle}</TableCell>
                          <TableCell>{row.batch_id}</TableCell>
                          <TableCell>{row.job_id}</TableCell>
                          <TableCell>{row.source}</TableCell>
                          <TableCell>{row.started_at}</TableCell>
                          <TableCell>{row.finished_at}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default ProgressInfo;
